import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
    LogOut,
    Users,
    Package,
    ShoppingBag,
    Loader2,
    Mail,
    MapPin,
    ArrowLeft,
    Boxes,
    Receipt,
    Sparkles,
    LayoutDashboard,
    BarChart3,
    UserCircle,
    Ticket,
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import AdminOrders from "./AdminOrders";
import AdminStock from "./AdminStock";
import AdminContent from "./AdminContent";
import AdminLocations from "../components/admin/AdminLocations";
import AdminProducts from "../components/admin/AdminProducts";
import AdminKits from "../components/admin/AdminKits";
import AdminSettings from "../components/admin/AdminSettings";
import AdminDashboardHome from "./AdminDashboardHome";
import AdminAnalytics from "./AdminAnalytics";
import AdminCustomers from "./AdminCustomers";
import AdminLeads from "./AdminLeads";
import AdminCoupons from "./AdminCoupons";
import NotificationBell from "../components/admin/NotificationBell";

const TABS = [
    { k: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { k: "analytics", label: "Analytics", icon: BarChart3 },
    { k: "orders", label: "Encomendas", icon: Receipt },
    { k: "customers", label: "Clientes", icon: UserCircle },
    { k: "leads", label: "Leads", icon: Users },
    { k: "coupons", label: "Cupões", icon: Ticket },
    { k: "stock", label: "Stock", icon: Boxes },
    { k: "products", label: "Produtos", icon: Package },
    { k: "kits", label: "Kits", icon: ShoppingBag },
    { k: "locations", label: "Pontos de Venda", icon: MapPin },
    { k: "content", label: "Conteúdo", icon: Sparkles },
    { k: "settings", label: "Definições", icon: Mail },
];

export default function AdminDashboard() {
    const { user, loading, logout, apiAuth } = useAdminAuth();
    const [tab, setTab] = useState("dashboard");

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-brand-green/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-2" data-testid="admin-logo-link">
                            <img
                                src="/brand/logo-main.png"
                                alt="Sabor Líquido"
                                className="h-9 w-auto"
                            />
                        </Link>
                        <span className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-brand-green-soft px-3 py-1 text-xs font-bold uppercase tracking-widest text-brand-green">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <NotificationBell apiAuth={apiAuth} onNavigate={setTab} />
                        <Link
                            to="/"
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-4 py-2 text-xs font-semibold transition-colors"
                            data-testid="admin-back-site"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Ir para o site
                        </Link>
                        <button
                            type="button"
                            onClick={logout}
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink hover:bg-red-500 text-white px-4 py-2 text-xs font-semibold transition-colors"
                            data-testid="admin-logout"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sair
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">
                            Olá, {user?.name || "Admin"}
                        </div>
                        <h1 className="mt-1 font-display font-medium text-3xl md:text-4xl text-brand-ink">
                            Backoffice Sabor Líquido
                        </h1>
                    </div>
                </div>

                {/* Tabs */}
                <nav className="mt-6 flex gap-2 flex-wrap" aria-label="Admin sections">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.k;
                        return (
                            <button
                                key={t.k}
                                type="button"
                                onClick={() => setTab(t.k)}
                                data-testid={`admin-tab-${t.k}`}
                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all ${
                                    active
                                        ? "bg-brand-green text-white shadow-pop"
                                        : "bg-white text-brand-ink hover:bg-brand-green-soft"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                {t.label}
                            </button>
                        );
                    })}
                </nav>

                <div className="mt-6">
                    {tab === "dashboard" && (
                        <AdminDashboardHome apiAuth={apiAuth} onNavigate={setTab} />
                    )}
                    {tab === "analytics" && <AdminAnalytics apiAuth={apiAuth} />}
                    {tab === "orders" && <AdminOrders apiAuth={apiAuth} />}
                    {tab === "customers" && <AdminCustomers apiAuth={apiAuth} />}
                    {tab === "leads" && <AdminLeads apiAuth={apiAuth} />}
                    {tab === "coupons" && <AdminCoupons apiAuth={apiAuth} />}
                    {tab === "stock" && <AdminStock apiAuth={apiAuth} />}
                    {tab === "products" && <AdminProducts apiAuth={apiAuth} />}
                    {tab === "kits" && <AdminKits apiAuth={apiAuth} />}
                    {tab === "locations" && <AdminLocations apiAuth={apiAuth} />}
                    {tab === "content" && <AdminContent apiAuth={apiAuth} />}
                    {tab === "settings" && <AdminSettings apiAuth={apiAuth} />}
                </div>
            </main>
        </div>
    );
}
